import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Loader2, Save, ArrowLeft, ShieldCheck, Shield, Check } from 'lucide-react'
import { apiGet, apiPost, apiPut } from '../lib/api'

interface PermRow {
  id: number
  key: string
  label: string
  category: string
}

interface RoleRow {
  id: number
  name: string
  description: string
  is_system: boolean
  created_at: string | null
  permissions: number[]
  user_count: number
}

interface PermsResp {
  data: PermRow[]
}
interface RolesResp {
  data: RoleRow[]
}

export function RolEditor() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const params = useParams({ strict: false }) as { id?: string }
  const isNew = !params.id || params.id === 'nuevo'
  const roleId = !isNew ? parseInt(String(params.id), 10) : null

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [formError, setFormError] = useState('')

  const { data: permsData, isLoading: loadingPerms } = useQuery<PermsResp>({
    queryKey: ['admin-permissions'],
    queryFn: () => apiGet('/api/admin/permissions'),
  })

  const { data: rolesData, isLoading: loadingRoles } = useQuery<RolesResp>({
    queryKey: ['admin-roles'],
    queryFn: () => apiGet('/api/admin/roles'),
    enabled: !isNew,
  })

  const editingRole = useMemo(() => {
    if (isNew || !rolesData) return null
    return rolesData.data.find((r) => r.id === roleId) || null
  }, [isNew, roleId, rolesData])

  // Hidratar form cuando llegan datos del rol existente
  useEffect(() => {
    if (editingRole) {
      setName(editingRole.name)
      setDescription(editingRole.description)
      setSelected(new Set(editingRole.permissions))
    }
  }, [editingRole])

  // Agrupar permisos por categoría
  const grouped = useMemo(() => {
    const m = new Map<string, PermRow[]>()
    for (const p of permsData?.data || []) {
      const arr = m.get(p.category) || []
      arr.push(p)
      m.set(p.category, arr)
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [permsData])

  function togglePerm(id: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleCategory(perms: PermRow[]) {
    const allSelected = perms.every((p) => selected.has(p.id))
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        for (const p of perms) next.delete(p.id)
      } else {
        for (const p of perms) next.add(p.id)
      }
      return next
    })
  }

  function selectAll() {
    setSelected(new Set((permsData?.data || []).map((p) => p.id)))
  }
  function clearAll() {
    setSelected(new Set())
  }

  const createMut = useMutation({
    mutationFn: () =>
      apiPost('/api/admin/roles', {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selected),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      navigate({ to: '/roles' as any })
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMut = useMutation({
    mutationFn: () =>
      apiPut(`/api/admin/roles/${roleId}`, {
        name: editingRole?.is_system ? undefined : name.trim(),
        description: description.trim(),
        permissions: Array.from(selected),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-roles'] })
      navigate({ to: '/roles' as any })
    },
    onError: (e: Error) => setFormError(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError('')
    if (!editingRole?.is_system) {
      if (!name.trim()) return setFormError('El nombre es requerido')
      if (!/^[a-z0-9_]+$/.test(name.trim())) {
        return setFormError('Nombre: solo minúsculas, números o guión bajo')
      }
    }
    if (isNew) createMut.mutate()
    else updateMut.mutate()
  }

  const isSaving = createMut.isPending || updateMut.isPending
  const isLoading = loadingPerms || (!isNew && loadingRoles)

  return (
    <>
      <Topbar
        title={isNew ? 'Nuevo rol' : `Editar rol${editingRole ? `: ${editingRole.name}` : ''}`}
        subtitle={
          editingRole?.is_system
            ? 'Rol del sistema · solo se pueden modificar los permisos'
            : 'Define el nombre y los permisos que tendrá este rol'
        }
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: '/roles' as any })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </button>
        }
      />
      <main className="p-6">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}

        {!isLoading && (
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-1">
                <h2 className="mb-3 text-sm font-semibold text-ink-900">Datos del rol</h2>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-ink-700">
                      Nombre interno{' '}
                      {editingRole?.is_system && (
                        <span className="font-normal text-ink-500">(bloqueado)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={!!editingRole?.is_system}
                      placeholder="ej. soporte_nivel_1"
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-brand-500 disabled:bg-ink-50 disabled:text-ink-500"
                    />
                    <p className="mt-1 text-[11px] text-ink-500">
                      Minúsculas, números o guión bajo. Sin espacios.
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-ink-700">
                      Descripción
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Qué hace este rol…"
                      className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                    />
                  </div>
                  {editingRole && (
                    <div className="rounded-lg border border-ink-100 bg-ink-50 p-3 text-xs text-ink-700">
                      <div className="mb-1 flex items-center gap-2">
                        {editingRole.is_system ? (
                          <Badge variant="info">
                            <ShieldCheck className="size-3" /> sistema
                          </Badge>
                        ) : (
                          <Badge variant="default">
                            <Shield className="size-3" /> custom
                          </Badge>
                        )}
                      </div>
                      <div>
                        <strong>{editingRole.user_count}</strong> usuario(s) usan este rol.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-ink-900">
                    Permisos asignados{' '}
                    <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 font-mono text-xs text-brand-700">
                      {selected.size} / {permsData?.data.length || 0}
                    </span>
                  </h2>
                  <div className="flex gap-1 text-xs">
                    <button
                      type="button"
                      onClick={selectAll}
                      className="rounded-md border border-ink-200 bg-white px-2 py-1 font-medium text-ink-700 hover:bg-ink-50"
                    >
                      Seleccionar todo
                    </button>
                    <button
                      type="button"
                      onClick={clearAll}
                      className="rounded-md border border-ink-200 bg-white px-2 py-1 font-medium text-ink-700 hover:bg-ink-50"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {grouped.map(([cat, perms]) => {
                    const allSelected = perms.every((p) => selected.has(p.id))
                    const someSelected = perms.some((p) => selected.has(p.id))
                    return (
                      <div key={cat} className="rounded-xl border border-ink-100">
                        <div className="flex items-center justify-between border-b border-ink-100 bg-ink-50 px-3 py-2">
                          <button
                            type="button"
                            onClick={() => toggleCategory(perms)}
                            className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${
                              allSelected
                                ? 'text-brand-700'
                                : someSelected
                                ? 'text-amber-700'
                                : 'text-ink-600'
                            } hover:underline`}
                          >
                            <span
                              className={`grid size-4 place-items-center rounded border ${
                                allSelected
                                  ? 'border-brand-600 bg-brand-600 text-white'
                                  : someSelected
                                  ? 'border-amber-400 bg-amber-100'
                                  : 'border-ink-300 bg-white'
                              }`}
                            >
                              {allSelected && <Check className="size-3" strokeWidth={3} />}
                              {!allSelected && someSelected && (
                                <span className="size-1.5 rounded-sm bg-amber-600" />
                              )}
                            </span>
                            {cat}
                          </button>
                          <span className="text-[11px] text-ink-500">
                            {perms.filter((p) => selected.has(p.id)).length} / {perms.length}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 gap-1 p-2 md:grid-cols-2">
                          {perms.map((p) => {
                            const on = selected.has(p.id)
                            return (
                              <label
                                key={p.id}
                                className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                                  on ? 'bg-brand-50' : 'hover:bg-ink-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => togglePerm(p.id)}
                                  className="mt-0.5 size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                                />
                                <span className="flex-1">
                                  <span className="block text-ink-900">{p.label}</span>
                                  <span className="block font-mono text-[11px] text-ink-500">
                                    {p.key}
                                  </span>
                                </span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-ink-100 pt-4">
              <button
                type="button"
                onClick={() => navigate({ to: '/roles' as any })}
                className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {isNew ? 'Crear rol' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </main>
    </>
  )
}

export default RolEditor
